import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Col, Row, Badge, Input } from 'reactstrap';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const FAQDetails = ({ reactTableData, isNoRecordFound, onEdit }) => {
  const [rows] = useState(reactTableData && reactTableData?.tableRowsData ? reactTableData.tableRowsData : []);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('All');
  const [expandedIndex, setExpandedIndex] = useState(null);

  // 1. Extract Unique Keywords for Filter Tags
  const keywords = useMemo(() => {
    const list = new Set();
    rows.forEach(item => {
      if (item.keyword) {
        list.add(item.keyword.trim());
      }
    });
    return ['All', ...Array.from(list)];
  }, [rows]);

  // 2. Filter Rows by Search Query & Active Keyword Tag
  const filteredRows = useMemo(() => {
    return rows.filter(item => {
      const q = (item.question || '').toLowerCase();
      const a = (item.answer || '').toLowerCase();
      const k = (item.keyword || '').toLowerCase();
      const search = searchQuery.toLowerCase();
      
      const matchesSearch = q.includes(search) || a.includes(search) || k.includes(search);
      const matchesKeyword = activeKeyword === 'All' || (item.keyword && item.keyword.trim() === activeKeyword);
      
      return matchesSearch && matchesKeyword;
    });
  }, [rows, searchQuery, activeKeyword]);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Styling maps
  const cardStyle = (isExpanded) => ({
    borderRadius: '12px',
    border: isExpanded ? '1px solid #6366f1' : '1px solid #e2e8f0',
    background: '#ffffff',
    boxShadow: isExpanded 
      ? '0 10px 15px -3px rgba(99, 102, 241, 0.1), 0 4px 6px -2px rgba(99, 102, 241, 0.05)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    marginBottom: '15px'
  });

  const accordionBodyStyle = (isExpanded) => ({
    maxHeight: isExpanded ? '1000px' : '0px',
    opacity: isExpanded ? 1 : 0,
    overflow: 'hidden',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    padding: isExpanded ? '18px 24px 24px' : '0px 24px 0px',
    borderTop: isExpanded ? '1px solid #f1f5f9' : 'none',
    backgroundColor: '#fafcff',
    color: '#334155',
    fontSize: '0.95rem',
    lineHeight: '1.65'
  });

  return (
    <Col md={12} lg={12}>
      {/* Dynamic Header Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        borderRadius: '16px',
        padding: '24px 20px',
        textAlign: 'center',
        color: '#ffffff',
        marginBottom: '25px',
        boxShadow: '0 8px 20px -5px rgba(124, 58, 237, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '850px',
        margin: '0 auto 25px'
      }}>
        <div style={{ position: 'absolute', top: '-40%', left: '-10%', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: '-60%', right: '-5%', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
          <HelpOutlineIcon style={{ fontSize: '1.8rem', color: 'rgba(255, 255, 255, 0.95)' }} />
          <h2 style={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.5px', margin: 0, color: '#ffffff' }}>FAQ Help Center</h2>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.92rem', maxWidth: '620px', margin: '0 auto 16px' }}>
          Have questions? Find quick answers to common queries about Pick & Drop operations below.
        </p>
        
        {/* Modern Search Bar */}
        <div style={{ maxWidth: '520px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div className="input-group" style={{ borderRadius: '30px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.12)', border: 'none' }}>
            <span className="input-group-text bg-white border-0 ps-4 pr-1">
              <SearchIcon style={{ color: '#64748b' }} />
            </span>
            <Input
              type="text"
              placeholder="Search FAQs by keyword, question, or answer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                border: 'none',
                height: '45px',
                fontSize: '0.95rem',
                paddingLeft: '8px',
                boxShadow: 'none',
                borderRadius: '0 30px 30px 0',
                color: '#1e293b'
              }}
            />
            {searchQuery && (
              <span className="input-group-text bg-white border-0 pe-4" style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')}>
                <ClearIcon style={{ color: '#94a3b8', fontSize: '1.2rem' }} />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filter Keywords Tags Row */}
      {keywords.length > 2 && (
        <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center" style={{ maxWidth: '850px', margin: '0 auto 24px' }}>
          {keywords.map(kw => (
            <button
              key={kw}
              onClick={() => {
                setActiveKeyword(kw);
                setExpandedIndex(null);
              }}
              style={{
                padding: '6px 16px',
                borderRadius: '20px',
                border: activeKeyword === kw ? '1.5px solid #6366f1' : '1.5px solid #cbd5e1',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                background: activeKeyword === kw ? '#6366f1' : '#ffffff',
                color: activeKeyword === kw ? '#ffffff' : '#475569',
                transition: 'all 0.2s ease',
                boxShadow: activeKeyword === kw ? '0 4px 10px rgba(99, 102, 241, 0.2)' : 'none'
              }}
            >
              {kw}
            </button>
          ))}
        </div>
      )}

      {/* FAQs List */}
      {!isNoRecordFound && filteredRows.length > 0 ? (
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          {filteredRows.map((item, idx) => {
            const isExpanded = expandedIndex === idx;
            return (
              <div key={item.faqId || idx} style={cardStyle(isExpanded)}>
                {/* Header / Question row */}
                <div 
                  onClick={() => toggleExpand(idx)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    background: isExpanded ? '#f8faff' : '#ffffff',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexGrow: 1, paddingRight: '15px' }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="text-muted fw-bold" style={{ fontSize: '0.85rem', color: '#6366f1' }}>Q.</span>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.02rem', color: '#1e293b' }}>
                        {item.question}
                      </h5>
                    </div>
                    {item.keyword && (
                      <div className="mt-1">
                        <Badge bg="secondary" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px' }}>
                          {item.keyword}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Actions & Chevron */}
                  <div className="d-flex align-items-center gap-2">
                    {onEdit && (
                      <Tooltip title="Edit FAQ Details" arrow>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(item);
                          }}
                          style={{ color: '#6366f1' }}
                        >
                      
                        </IconButton>
                      </Tooltip>
                    )}
                    <ExpandMoreIcon 
                      style={{ 
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                        transition: 'transform 0.25s',
                        color: '#64748b' 
                      }} 
                    />
                  </div>
                </div>

                {/* Collapsible Body / Answer */}
                <div style={accordionBodyStyle(isExpanded)}>
                  <p style={{ whiteSpace: 'pre-line', margin: 0 }}>
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm text-center py-5" style={{ borderRadius: '12px' }}>
          <CardBody>
            <div style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '10px' }}>🔍</div>
            <h5 className="text-muted fw-bold">No FAQs Found</h5>
            <p className="text-muted small">Try checking your spelling or using different keywords.</p>
          </CardBody>
        </Card>
      )}
    </Col>
  );
};

FAQDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  isNoRecordFound: PropTypes.bool,
  onEdit: PropTypes.func,
};

export default FAQDetails;
